import { Fragment, useState, type ReactNode } from "react";
import { api, type Client, type ClientAccess, type ClientKind } from "../lib/api";
import { useAction, useResource } from "../lib/hooks";
import { Btn, ErrorNote, Facts, Field, Loading, Notice, PageHead, Section } from "../components/kit";
import { Segmented } from "./ClientDetail";

interface ConnectRequest {
  app_name: string;
  app_url: string;
  host: string;
  redirect_uris: string[];
  post_logout_redirect_uris: string[];
  return_to: string;
  suggested_id: string;
}

interface ConnectView {
  request: ConnectRequest;
  issuer: string;
  existing?: Client;
}

/**
 * An app (such as Silah) asking to become a portal. It sent the
 * administrator here; approving creates the portal, or gives an existing one
 * a new secret, and sends the browser back to the app, whose server then
 * collects the credentials itself. Nobody copies a secret.
 */
export function ConnectPage() {
  const query = window.location.search;
  const { data, error } = useResource<ConnectView>(`/connect${query}`);
  if (error) {
    return (
      <>
        <PageHead
          eyebrow="Connect an app"
          title="This request cannot be approved"
          lede="Go back to the app and start again. If it keeps happening, the app's developer needs the message below."
        />
        <Notice tone="danger">{error.message}</Notice>
      </>
    );
  }
  return data ? <Decide view={data} query={query} /> : <Loading />;
}

function Decide({ view, query }: { view: ConnectView; query: string }) {
  const { request: req, existing } = view;
  const [id, setID] = useState(req.suggested_id);
  const [kind, setKind] = useState<ClientKind>(existing?.kind ?? "group");
  const [access, setAccess] = useState<ClientAccess>(existing?.access ?? "everyone");
  const act = useAction();
  const matches = existing && existing.id === id;

  const answer = async (approve: boolean) => {
    const out = await act.run(() => api<{ redirect: string }>("POST", "/connect", { query, approve, client_id: id, kind, access }));
    // Back to the app: with a code it redeems itself, or with access_denied.
    if (out) window.location.assign(out.redirect);
  };

  return (
    <>
      <PageHead
        eyebrow="Connect an app"
        title={`${req.app_name} wants to sign people in with Mashriqain ID`}
        lede={
          <>
            Approving makes <code>{req.host}</code> a portal. People who choose it are sent to id.mashriqain.com, and the app learns their name,
            email and phone number. It can only ever send them back to addresses on {req.host}.
          </>
        }
      />
      <Section title="What it asks for">
        <Facts
          items={[
            ["App", <code key="a">{req.app_url}</code>],
            ["Sign-in returns to", <RawList key="r" items={req.redirect_uris} />],
            ...(req.post_logout_redirect_uris.length ? ([["After sign-out", <RawList key="l" items={req.post_logout_redirect_uris} />]] as [string, ReactNode][]) : []),
          ]}
        />
      </Section>
      <Section title="As a portal">
        <form
          className="form"
          onSubmit={(e) => {
            e.preventDefault();
            void answer(true);
          }}
        >
          <div className="form-grid">
            <Field label="Portal ID" hint="Lowercase letters, digits and hyphens." error={act.error?.fields.id}>
              <input className="input code" value={id} onChange={(e) => setID(e.target.value.toLowerCase())} required pattern="[a-z][a-z0-9-]{1,62}" />
            </Field>
            <Field label="Whose site">
              <Segmented
                value={kind}
                onChange={(v) => {
                  setKind(v);
                  if (!matches) setAccess(v === "customer" ? "listed" : "everyone");
                }}
                options={[
                  ["group", "A Mashriqain site"],
                  ["customer", "A customer's site"],
                ]}
              />
            </Field>
            <Field label="Who may sign in" hint={access === "listed" ? "Only accounts you add on the portal's page afterwards." : "Any enabled Mashriqain account."}>
              <Segmented
                value={access}
                onChange={setAccess}
                options={[
                  ["everyone", "Every account"],
                  ["listed", "Listed accounts"],
                ]}
              />
            </Field>
          </div>
          {matches && (
            <Notice tone="warn" title={`The portal ${existing.id} already exists.`}>
              Approving keeps it and its addresses, adds the ones above, and gives it a new secret. The old secret stops working at once, so only approve
              if {req.app_name} is reconnecting.
            </Notice>
          )}
          <ErrorNote error={act.error} />
          <div className="form-actions">
            <Btn variant="primary" type="submit" disabled={act.busy}>
              {matches ? "Reconnect" : "Approve"}
            </Btn>
            <Btn disabled={act.busy} onClick={() => void answer(false)}>
              Deny
            </Btn>
          </div>
        </form>
      </Section>
    </>
  );
}

function RawList({ items }: { items: string[] }) {
  return (
    <>
      {items.map((u, i) => (
        <Fragment key={u}>
          {i > 0 && <br />}
          <code>{u}</code>
        </Fragment>
      ))}
    </>
  );
}
